import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Time value parsing and formatting utilities
 */
export const parseTimeValue = (value: string | Dayjs | null): Dayjs | null => {
  if (!value) return null;

  try {
    return dayjs.isDayjs(value) ? value : dayjs.utc(value).local();
  } catch {
    return null;
  }
};

export const formatTimeForBackend = (time: Dayjs): string => {
  // Anchor time-of-day to a fixed date and store in UTC (matches FieldBank format)
  return dayjs
    .utc('1970-01-01T00:00:00Z')
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
    .toISOString();
};

export const formatTimeForDisplay = (value: string): string => {
  try {
    const time = dayjs.utc(value).local();
    if (time.isValid()) {
      return time.format('HH:mm');
    }
    return value;
  } catch {
    return value;
  }
};

/**
 * Date value parsing and formatting utilities
 */
export const parseDateValue = (value: string | Dayjs | null): Dayjs | null => {
  if (!value || value.toString().trim() === '') return null;

  try {
    // If it's already a Dayjs object, return it
    if (dayjs.isDayjs(value)) {
      return value.isValid() ? value : null;
    }

    const dateStr = String(value);

    if (dateStr.includes('Z')) {
      // UTC ISO string from backend: Parse as UTC and convert to local time
      // This ensures the date displays correctly in the user's timezone.
      // Example: 2025-12-15T08:00:00.000Z (stored as PT midnight) → 2025-12-15 00:00:00 local
      const date = dayjs.utc(dateStr).local();
      return date.isValid() ? date : null;
    }

    // For plain date strings (YYYY-MM-DD from forms), parse normally as local date
    const date = dayjs(value);
    return date.isValid() ? date : null;
  } catch {
    return null;
  }
};

export const formatDateForDisplay = (value: string): string => {
  try {
    const date = value.includes('Z') || value.includes('+') || value.includes('T')
      ? dayjs.utc(value).local()
      : dayjs(value);
    
    if (date.isValid()) {
      return date.format('MM/DD/YYYY');
    }
    return value;
  } catch {
    return value;
  }
};

/**
 * Number input validation
 */
export const validateNumberInput = (value: string): boolean => {
  return /^\d*$/.test(value);
};

export const sanitizeNumberInput = (value: string): string => {
  return validateNumberInput(value) ? value : '';
};

/**
 * Boolean value handling
 */
export const parseBooleanValue = (value: string | boolean): boolean => {
  if (typeof value === 'boolean') return value;
  return value === 'true' || value === 'True';
};

export const formatBooleanForBackend = (value: boolean): string => {
  return value ? 'true' : 'false';
};

export const formatBooleanForDisplay = (value: string): string => {
  const boolValue = parseBooleanValue(value);
  return boolValue ? 'True' : 'False';
};

/**
 * Field value formatting utilities (accepts string or string[] for array default values)
 */
export const formatFieldValueForDisplay = (value: string | string[], fieldType: string): string => {
  const arr = Array.isArray(value) ? value : [value];
  if (arr.length === 0 || (arr.length === 1 && !arr[0]?.trim())) {
    return '';
  }
  if (fieldType === 'MultiSelect') {
    return arr.filter(Boolean).join(', ');
  }
  const first = arr[0] ?? '';
  switch (fieldType) {
    case 'Date':
      return formatDateForDisplay(first);
    case 'Time':
      return formatTimeForDisplay(first);
    case 'Boolean':
      return formatBooleanForDisplay(first);
    default:
      return first;
  }
};

/**
 * Date range constants (matching FieldBank)
 */
export const DATE_RANGE = {
  MIN: dayjs.utc('1900-01-01'),
  MAX: dayjs.utc('2199-12-31'),
};

/**
 * Common text field styles
 */
export const getCommonTextFieldStyles = () => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'background.paper',
    fontSize: '0.875rem',
  },
});
